import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { RequirementsProps } from '../../tools/context';
import { eventTracking } from '../../tools/eventTracking';
import { mergeResponsiveSpacingVars } from '../../tools/responsiveSpacing';
import { imageUrlFor } from '../../tools/sanity';
import { Picto } from '../Picto';
import { Infinite, InfiniteHeart, StarCharentes } from '../Picto/charentes/Icons';

const MOBILE_MAX_WIDTH = 1024;

interface EventItem {
  image: any;
  mobileImage: any;
  title: string;
  subtitle: string;
  query?: string;
  redirectLink?: string;
}

interface EventsSectionProps {
  title: {
    text: string;
    icon?: string;
    titleTextColor?: any;
  };
  backgroundColor?: {
    hex: string;
  };
  items: EventItem[];
  requirements: RequirementsProps;
  margin?: string;
  marginMobile?: string;
  padding?: string;
  paddingMobile?: string;
  imageBorderRadius?: string;
}

const getThemeIcon = (iconKey?: string, fill?: string) => {
  switch (iconKey) {
    case 'Infinite':
      return <Infinite fill={fill} />;
    case 'star-charentes':
      return <StarCharentes fill={fill} />;
    case 'infinite-heart':
      return <InfiniteHeart fill={fill} />;
    default:
      return <InfiniteHeart fill={fill} />;
  }
};

export default function EventsSection({
  title,
  items,
  requirements,
  backgroundColor,
  margin,
  marginMobile,
  padding,
  paddingMobile,
  imageBorderRadius,
}: EventsSectionProps) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    containScroll: 'trimSnaps',
  });
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const background = backgroundColor?.hex ?? '#141040';
  const textColor = title.titleTextColor?.hex ?? '#D0DB5F';

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const onCarouselSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !isMobile) return;

    onCarouselSelect();
    emblaApi.on('select', onCarouselSelect);
    emblaApi.on('reInit', onCarouselSelect);

    return () => {
      emblaApi.off('select', onCarouselSelect);
      emblaApi.off('reInit', onCarouselSelect);
    };
  }, [emblaApi, isMobile, onCarouselSelect]);

  useEffect(() => {
    if (!emblaApi || !isMobile) return;
    emblaApi.reInit();
    onCarouselSelect();
  }, [emblaApi, isMobile, items.length, onCarouselSelect]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_MAX_WIDTH);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderMobileNav = () => {
    if (!isMobile || items.length <= 1) return null;

    return (
      <div className="eventsSection__nav" style={{ color: textColor }}>
        <button
          type="button"
          className="eventsSection__nav-btn"
          onClick={scrollPrev}
          disabled={!prevBtnEnabled}
          aria-label="Vignette précédente"
        >
          <Picto iconKey="arrow-left" fill={textColor} />
        </button>
        <button
          type="button"
          className="eventsSection__nav-btn"
          onClick={scrollNext}
          disabled={!nextBtnEnabled}
          aria-label="Vignette suivante"
        >
          <Picto iconKey="arrow-right" fill={textColor} />
        </button>
      </div>
    );
  };

  const renderContent = () => {
    if (isMobile) {
      return (
        <div className="embla eventsSection__embla" ref={emblaRef}>
          <div className="embla__container">
            {items.map((item, index) => (
              <div
                key={index}
                className={`embla__slide${selectedIndex === index ? ' embla__slide--active' : ''}`}
              >
                <a
                  onClick={() =>
                    eventTracking(
                      'interaction',
                      'clic-suggestion',
                      `${document.title} - ${item.title}`
                    )
                  }
                  href={
                    item.query
                      ? `/engine/charentes-engine/chat?chat_id=${uuidv4()}&query=${encodeURIComponent(item.query)}`
                      : item.redirectLink
                  }
                  className="content"
                >
                  <div
                    className="card"
                    style={{
                      backgroundPosition: 'bottom',
                      backgroundImage: item.mobileImage
                        ? `url(${imageUrlFor(item.mobileImage, requirements).width(238).format('webp').url()})`
                        : undefined,
                    }}
                  >
                    <div className="content">
                      <p className="cardTitle">{item.title}</p>
                      <p className="subtitle">{item.subtitle}</p>
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={`grid${items.length < 6 ? ' line' : ''}`}>
        {items.map((item, index) => (
          <div
            key={index}
            className="card"
            style={{
              backgroundImage: item.image
                ? `url(${imageUrlFor(item.image, requirements).width(220).format('webp').url()})`
                : undefined,
            }}
            onClick={() => {
              eventTracking('interaction', 'clic-suggestion', `${document.title} - ${item.title}`);
              if (item.query) {
                window.location.href = `/engine/charentes-engine/chat?chat_id=${uuidv4()}&query=${encodeURIComponent(item.query)}`;
              } else if (item.redirectLink) {
                window.location.href = item.redirectLink;
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="overlay"></div>
            <div className="content">
              <p className="cardTitle">{item.title}</p>
              <p className="subtitle">{item.subtitle}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const sectionStyle: React.CSSProperties = {
    backgroundColor: background,
    ...mergeResponsiveSpacingVars('events-section', {
      margin,
      marginMobile,
      padding,
      paddingMobile,
    }),
    ...(imageBorderRadius
      ? ({
          ['--events-section-image-border-radius' as any]: imageBorderRadius,
        } as React.CSSProperties)
      : {}),
  };

  return (
    <section className="eventsSection" style={sectionStyle}>
      <div className="container">
        <div
          className={`eventsSection__header${
            isMobile && items.length > 1 ? ' eventsSection__header--with-nav' : ''
          }`}
        >
          <h2 style={{ color: textColor }}>
            {getThemeIcon(title?.icon, textColor)}
            <span className="eventsSection__title">{title?.text ?? ''}</span>
          </h2>
          {renderMobileNav()}
        </div>
        {renderContent()}
      </div>
    </section>
  );
}
